/**
 * Marketing i18n fragment — intgYouforce (integrations/youforce).
 *
 * Source of truth for every claim on this page: content/docs/{en,nl}/admin/
 * hr-governance.md § "Connecting AFAS Profit and Visma Raet Youforce" and the
 * shipped code in src/server/hr/youforce-connector.ts + infra-credentials.ts.
 * Only shipped behaviour is described: OAuth2 client-credentials against Visma
 * Connect, operator-supplied endpoint templates pinned to the base URL's host
 * over https, the same prescribed field contract as AFAS, one-person filtering
 * with explicit projection, per-organization credentials with all-or-nothing
 * resolution, the single `connection` health capability (a forced token
 * refresh, which validates the credential and NOT the endpoints), honest
 * failure with reason codes, and the governed answer path on top.
 * Deliberately NOT claimed: any write-back, salary amounts or payslips, a
 * self-service connection form, per-read health lanes, prescriptions about
 * which Youforce domain API you expose, or a livetest against a real Youforce
 * environment.
 */

export const intgYouforceEn = {
  meta: {
    title: "Visma Raet Youforce integration — read-only HR data behind an approval model",
    description:
      "FixControl reads a named set of fields from the Youforce domain-API endpoints you expose — remaining leave hours with an as-of date, a payroll explanation code — filters every call on one person, and writes nothing back. An answer about someone's own situation is a draft until a second person approves it.",
  },
  hero: {
    eyebrow: "Visma Raet Youforce integration",
    title: "Youforce data read-only,",
    highlight: "answers only after approval",
    subtitle:
      "An employee of your organization asks about their leave balance or a line on their payslip. FixControl reads the named fields it needs from the Youforce endpoints you expose, filtered on that one person, and writes nothing back. The answer about their own situation is a draft until someone on your team approves it.",
    primaryCta: "Book a demo",
    secondaryCta: "See the HR permission model",
    bullet1: "Read-only — nothing is written back",
    bullet2: "Named fields, not a personnel file",
    bullet3: "Credentials per organization",
  },
  what: {
    eyebrow: "What it does",
    title: "What the Visma Raet Youforce connection covers",
  },
  features: {
    readOnly: {
      title: "Read-only by construction",
      body: "The connection has read methods and nothing else. No leave request, no profile change, no record created in Youforce — FixControl reads what an answer needs and leaves your HR system exactly as it was.",
    },
    endpoints: {
      title: "Endpoints you expose, on templates you control",
      body: "FixControl does not prescribe which Youforce domain API you run. You give it three endpoint templates — employee, leave, payroll — with a placeholder for the person, and it reads the prescribed field ids back from whatever you expose there.",
    },
    hostPinning: {
      title: "Templates pinned to your own host",
      body: "A template must stay on the same host as the domain-API base URL and travel over https. A foreign host, a protocol-relative URL or an unresolved placeholder is refused before a single request is made — a typo in a template cannot ship your bearer token somewhere else.",
    },
    credentials: {
      title: "Credentials per organization",
      body: "An OAuth2 client id and secret — the secret encrypted at rest — plus the token endpoint and the domain-API base URL. A half-filled entry is ignored rather than merged with platform defaults, so one environment's secret can never be paired with another environment's host.",
    },
    health: {
      title: "A connection check on the credential",
      body: "A periodic check forces a fresh token exchange, so a cached token cannot keep a badge green. A revoked client or a rotated secret surfaces as a failing check, an audit event and an administrator notification. The check covers the credential — it does not walk your endpoint templates.",
    },
    governedAnswer: {
      title: "The data is read; the answer still needs a person",
      body: "What comes back from Youforce does not become an outgoing answer on its own. A question about someone's own situation becomes a draft or a review case, bound to a named reviewer, and a second person approves before it is delivered.",
    },
  },
  capabilities: {
    eyebrow: "Capabilities & health",
    title: "What the Youforce connection can and cannot do",
    description:
      "The connection has one health capability on purpose: the connection itself. A single missing employee record is a data condition, not a broken integration, so it routes that one question to a person instead of colouring the connection red for everyone.",
    leaveBalance: {
      name: "Remaining leave hours",
      body: "Read from the leave endpoint you expose, with the as-of date that belongs to the value. Available where that template resolves and returns the prescribed field ids.",
    },
    payrollCode: {
      name: "Payroll explanation code",
      body: "An explanation code with an as-of date — never an amount, never a payslip document. Reading it also requires the separate payroll permission on the reader's project role.",
    },
    hostPinning: {
      name: "Same-host, https-only templates",
      body: "Not configurable away. A template on a foreign host, a protocol-relative URL or a placeholder that stayed unresolved is refused before the request leaves FixControl.",
    },
    connectionCheck: {
      name: "Connection check (credential)",
      body: "A forced token refresh proves the client id and secret still work. It deliberately touches no data endpoint, so a green check confirms the credential — not that every endpoint template resolves.",
    },
    writeBack: {
      name: "Writing anything back to Youforce",
      body: "Not part of the connection, at any level. There is no create, update or delete path — no leave request, no absence registration, no profile change.",
    },
    salaryAmounts: {
      name: "Salary amounts & payslip documents",
      body: "Out of scope on purpose. The payroll endpoint returns an explanation code and its as-of date; amounts and documents are not read, so they cannot appear in an answer or in an audit record.",
    },
  },
  governed: {
    eyebrow: "When Youforce cannot answer",
    title: "A missing value never becomes a made-up one",
    noteTitle: "Honest failure, with a reason on record",
    noteBody:
      "A missing record, a value that cannot be parsed, an unexpected response shape and an unreachable endpoint all lead to the same place: the question is handed to a person, and the reason is recorded. No number is invented, and a leave balance is never quietly defaulted to zero. Error messages carry a stable code and at most an HTTP status — never a token, a URL, a response body or an e-mail address. The model never sees any of this: it receives the question text to propose a classification, and personnel data, retrieved documents and answer bodies are not sent to a model at all.",
  },
  faq: {
    eyebrow: "FAQ",
    title: "Questions about the Visma Raet Youforce integration",
    q1: {
      q: "Does FixControl write anything back to Youforce?",
      a: "No. The connection is read-only by construction: it has read methods and no create, update or delete path. An employee cannot request leave or change a record through FixControl, and no answer flow can either.",
    },
    q2: {
      q: "Which fields does FixControl read?",
      a: "The ones your three endpoints return under prescribed field ids: employee id, e-mail, employment percentage, employment type and collective agreement; remaining leave hours with an as-of date; and a payroll explanation code with an as-of date. Never a salary amount, never a payslip document.",
    },
    q3: {
      q: "Which Youforce API do I have to expose?",
      a: "That is your choice — the connection is deliberately not tied to one domain API. You supply three URL templates relative to your domain-API base URL, each with a placeholder for the person. They have to stay on that same host and use https; anything else is refused before a request is made.",
    },
    q4: {
      q: "What does the connection check actually prove?",
      a: "That the client id and secret still exchange for a token, because the check forces a refresh instead of trusting a cached one. It touches no data endpoint on purpose, so it does not prove that every endpoint template resolves — a read failure on one endpoint still routes that question to a person, with its reason recorded.",
    },
    q5: {
      q: "What happens when we rotate the client secret?",
      a: "The new secret is picked up on the next connection build. Because the token cache is keyed by the token endpoint and the client id, rotating only the secret leaves an already-issued access token valid for the rest of its lifetime — rotate the client id alongside it when you need an immediate cut-off.",
    },
    q6: {
      q: "Does an answer about my leave balance go out automatically?",
      a: "No. Anything touching an employee's own situation becomes a draft or a review case. The approval is bound to that exact answer text, the person who requested it cannot decide it, it expires after 72 hours on the server, and delivery consumes it so a double click cannot send twice.",
    },
  },
  related: {
    hrGovernance: {
      label: "HR questions with the right permissions",
      description: "The permission model, assignment and approval around the answer.",
    },
    afas: {
      label: "AFAS Profit",
      description: "The same read-only contract on AFAS GetConnector views.",
    },
    approvalLedger: {
      label: "AI approval ledger",
      description: "Who approved what, from which channel, on which grounds.",
    },
  },
  cta: {
    title: "See what a Youforce-backed answer has to pass",
    description:
      "Book a demo and follow one leave question from the Youforce read through assignment, approval and delivery.",
    primary: "Book a demo",
    secondary: "Talk to us",
  },
};

export const intgYouforceNl = {
  meta: {
    title: "Visma Raet Youforce-integratie — alleen-lezen HR-gegevens achter een goedkeuringsmodel",
    description:
      "FixControl leest een benoemde set velden uit de Youforce-domein-API-endpoints die je zelf aanbiedt — resterende verlofuren met peildatum, een looncode — filtert elke aanroep op één persoon, en schrijft niets terug. Een antwoord over iemands eigen situatie blijft concept tot een tweede persoon het goedkeurt.",
  },
  hero: {
    eyebrow: "Visma Raet Youforce-integratie",
    title: "Youforce-gegevens alleen-lezen,",
    highlight: "antwoorden pas na goedkeuring",
    subtitle:
      "Een medewerker van je organisatie vraagt naar het verlofsaldo of een regel op de loonstrook. FixControl leest de benoemde velden die daarvoor nodig zijn uit de Youforce-endpoints die je aanbiedt, gefilterd op die ene persoon, en schrijft niets terug. Het antwoord over de eigen situatie blijft concept tot iemand van je team het goedkeurt.",
    primaryCta: "Boek een demo",
    secondaryCta: "Bekijk het HR-rechtenmodel",
    bullet1: "Alleen-lezen — er wordt niets teruggeschreven",
    bullet2: "Benoemde velden, geen personeelsdossier",
    bullet3: "Inloggegevens per organisatie",
  },
  what: {
    eyebrow: "Wat het doet",
    title: "Wat de Visma Raet Youforce-koppeling dekt",
  },
  features: {
    readOnly: {
      title: "Alleen-lezen, van bouw af aan",
      body: "De koppeling heeft leesmethoden en verder niets. Geen verlofaanvraag, geen profielwijziging, geen record dat in Youforce ontstaat — FixControl leest wat een antwoord nodig heeft en laat je HR-systeem precies zoals het was.",
    },
    endpoints: {
      title: "Endpoints die je zelf aanbiedt, op sjablonen die je beheert",
      body: "FixControl schrijft niet voor welke Youforce-domein-API je draait. Je geeft drie endpoint-sjablonen mee — medewerker, verlof, loon — met een plaatshouder voor de persoon, en FixControl leest daar de voorgeschreven veld-id's uit terug.",
    },
    hostPinning: {
      title: "Sjablonen vastgezet op je eigen host",
      body: "Een sjabloon moet op dezelfde host blijven als de basis-URL van de domein-API en via https lopen. Een vreemde host, een protocol-relatieve URL of een niet-ingevulde plaatshouder wordt geweigerd voordat er ook maar één verzoek uitgaat — een typefout in een sjabloon kan je bearer-token dus niet elders bezorgen.",
    },
    credentials: {
      title: "Inloggegevens per organisatie",
      body: "Een OAuth2 client-id en secret — het secret versleuteld opgeslagen — plus het token-endpoint en de basis-URL van de domein-API. Een half ingevulde invoer wordt genegeerd in plaats van samengevoegd met platformwaarden, zodat het secret van de ene omgeving nooit aan de host van een andere omgeving raakt.",
    },
    health: {
      title: "Een verbindingscontrole op de inloggegevens",
      body: "Een periodieke controle dwingt een verse tokenuitwisseling af, zodat een gecachet token geen badge groen kan houden. Een ingetrokken client of geroteerd secret komt boven als een falende controle, een auditgebeurtenis en een beheerdersmelding. De controle dekt de inloggegevens — ze loopt je endpoint-sjablonen niet langs.",
    },
    governedAnswer: {
      title: "De gegevens worden gelezen; het antwoord vraagt nog steeds een mens",
      body: "Wat Youforce teruggeeft, wordt niet uit zichzelf een uitgaand antwoord. Een vraag over de eigen situatie wordt een concept of een reviewzaak, gebonden aan een benoemde behandelaar, en een tweede persoon keurt goed voordat het wordt afgeleverd.",
    },
  },
  capabilities: {
    eyebrow: "Capabilities & health",
    title: "Wat de Youforce-koppeling wel en niet kan",
    description:
      "De koppeling heeft bewust één health-capability: de verbinding zelf. Eén ontbrekend medewerkerrecord is een datasituatie, geen kapotte koppeling — die ene vraag gaat naar een mens in plaats van dat de koppeling voor iedereen rood kleurt.",
    leaveBalance: {
      name: "Resterende verlofuren",
      body: "Gelezen uit het verlof-endpoint dat je aanbiedt, met de peildatum die bij de waarde hoort. Beschikbaar waar dat sjabloon oplost en de voorgeschreven veld-id's teruggeeft.",
    },
    payrollCode: {
      name: "Looncode",
      body: "Een uitlegcode met peildatum — nooit een bedrag, nooit een loonstrookdocument. Om hem te lezen is bovendien het aparte loonrecht op de projectrol van de lezer nodig.",
    },
    hostPinning: {
      name: "Sjablonen: zelfde host, alleen https",
      body: "Niet weg te configureren. Een sjabloon op een vreemde host, een protocol-relatieve URL of een plaatshouder die niet is ingevuld, wordt geweigerd voordat het verzoek FixControl verlaat.",
    },
    connectionCheck: {
      name: "Verbindingscontrole (inloggegevens)",
      body: "Een afgedwongen tokenvernieuwing bewijst dat client-id en secret nog werken. Ze raakt bewust geen data-endpoint aan, dus een groene controle bevestigt de inloggegevens — niet dat elk endpoint-sjabloon oplost.",
    },
    writeBack: {
      name: "Iets terugschrijven naar Youforce",
      body: "Geen onderdeel van de koppeling, op geen enkel niveau. Er is geen aanmaak-, wijzig- of verwijderpad — geen verlofaanvraag, geen verzuimregistratie, geen profielwijziging.",
    },
    salaryAmounts: {
      name: "Salarisbedragen & loonstrookdocumenten",
      body: "Bewust buiten scope. Het loon-endpoint geeft een uitlegcode met peildatum terug; bedragen en documenten worden niet gelezen, en kunnen dus niet in een antwoord of in een auditregel opduiken.",
    },
  },
  governed: {
    eyebrow: "Als Youforce geen antwoord kan geven",
    title: "Een ontbrekende waarde wordt nooit een verzonnen waarde",
    noteTitle: "Eerlijk falen, met de reden vastgelegd",
    noteBody:
      "Een ontbrekend record, een waarde die niet te lezen is, een onverwachte antwoordvorm en een onbereikbaar endpoint komen allemaal op dezelfde plek uit: de vraag gaat naar een mens, en de reden wordt vastgelegd. Er wordt geen getal verzonnen, en een verlofsaldo wordt nooit stilletjes op nul gezet. Foutmeldingen dragen een vaste code en hooguit een HTTP-status — nooit een token, een URL, een antwoordbody of een e-mailadres. Het model ziet dit alles niet: het krijgt de vraagtekst om een classificatie voor te stellen, en dossiergegevens, opgehaalde documenten en antwoordteksten gaan niet naar een model.",
  },
  faq: {
    eyebrow: "FAQ",
    title: "Vragen over de Visma Raet Youforce-integratie",
    q1: {
      q: "Schrijft FixControl iets terug naar Youforce?",
      a: "Nee. De koppeling is van bouw af aan alleen-lezen: ze heeft leesmethoden en geen aanmaak-, wijzig- of verwijderpad. Een medewerker kan via FixControl geen verlof aanvragen of een record wijzigen, en geen enkele antwoordroute kan dat ook.",
    },
    q2: {
      q: "Welke velden leest FixControl?",
      a: "Die je drie endpoints teruggeven onder voorgeschreven veld-id's: medewerker-id, e-mail, dienstverbandpercentage, dienstverbandsoort en cao; resterende verlofuren met peildatum; en een looncode met peildatum. Nooit een salarisbedrag, nooit een loonstrookdocument.",
    },
    q3: {
      q: "Welke Youforce-API moet ik aanbieden?",
      a: "Dat kies je zelf — de koppeling is bewust niet aan één domein-API vastgeklonken. Je geeft drie URL-sjablonen mee ten opzichte van de basis-URL van je domein-API, elk met een plaatshouder voor de persoon. Ze moeten op diezelfde host blijven en https gebruiken; al het andere wordt geweigerd voordat er een verzoek uitgaat.",
    },
    q4: {
      q: "Wat bewijst de verbindingscontrole precies?",
      a: "Dat client-id en secret nog een token opleveren, want de controle dwingt een vernieuwing af in plaats van een gecachet token te vertrouwen. Ze raakt met opzet geen data-endpoint aan, dus ze bewijst niet dat elk endpoint-sjabloon oplost — een leesfout op één endpoint stuurt die vraag alsnog naar een mens, met de reden vastgelegd.",
    },
    q5: {
      q: "Wat gebeurt er als we het client-secret roteren?",
      a: "Het nieuwe secret wordt opgepikt bij de volgende opbouw van de koppeling. Omdat de tokencache op het token-endpoint en het client-id is gesleuteld, blijft een al uitgegeven toegangstoken geldig voor de rest van zijn looptijd als je alleen het secret roteert — roteer het client-id mee als je direct wilt afkappen.",
    },
    q6: {
      q: "Gaat een antwoord over mijn verlofsaldo automatisch de deur uit?",
      a: "Nee. Alles wat de eigen situatie van een medewerker raakt, wordt een concept of een reviewzaak. De goedkeuring is gebonden aan precies die antwoordtekst, degene die haar aanvroeg kan er niet zelf over beslissen, ze vervalt na 72 uur op de server, en het afleveren verbruikt haar zodat dubbelklikken niet twee keer verstuurt.",
    },
  },
  related: {
    hrGovernance: {
      label: "HR-vragen met de juiste rechten",
      description: "Het rechtenmodel, de toewijzing en de goedkeuring rond het antwoord.",
    },
    afas: {
      label: "AFAS Profit",
      description: "Hetzelfde alleen-lezen-contract op AFAS GetConnector-overzichten.",
    },
    approvalLedger: {
      label: "AI-goedkeuringsgrootboek",
      description: "Wie keurde wat goed, vanuit welk kanaal, op welke gronden.",
    },
  },
  cta: {
    title: "Zie wat een antwoord uit Youforce moet passeren",
    description:
      "Boek een demo en volg één verlofvraag van de Youforce-leesactie tot en met toewijzing, goedkeuring en aflevering.",
    primary: "Boek een demo",
    secondary: "Neem contact op",
  },
};
